import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import { SEO } from "@/components/SEO";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { RelatedProducts } from "@/components/RelatedProducts";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { Footer } from "@/components/Footer";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import { 
  Lightbulb, Droplets, Sparkles, Shield, Award, 
  ArrowRight, Zap, Moon, Thermometer
} from "lucide-react";

// Import LUMA series images
import lumaSeriesProduct from "@/assets/luma-series-product.jpg";
import lumaSeriesProductSide from "@/assets/luma-series-product-side.jpg";
import lumaSeriesProductOpen from "@/assets/luma-series-product-open.jpg";
import lumaSeriesLifestyle from "@/assets/luma-series-lifestyle.jpg";
import aureumLogo from "@/assets/aureum-logo-new.png";

import { PriceFrom } from "@/components/products/PriceFrom";
const Luma = () => {
  useScrollToTop();

  const specifications = [
    { label: "Dimensions", value: "668mm × 388mm × 782mm" },
    { label: "Weight", value: "64kg" },
    { label: "Power Consumption", value: "1280W" },
    { label: "Water Usage", value: "4.4L/3.0L Dual Flush" },
    { label: "Warranty", value: "5 Years Comprehensive Coverage" },
    { label: "Installation", value: "Professional Installation Required" },
    { label: "LED Lighting", value: "Dynamic RGB with 16+ Color Modes" },
    { label: "Certification", value: "CE Certified, IPX4 Rated" }
  ];

  const features = [
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Dynamic LED Lighting",
      description: "Customizable RGB ambient lighting with 16+ color modes and brightness control for perfect bathroom ambiance"
    },
    {
      icon: <Moon className="w-8 h-8" />,
      title: "Smart Ambiance Control",
      description: "Intelligent lighting system that adjusts to time of day and user preferences for optimal comfort"
    },
    {
      icon: <Droplets className="w-8 h-8" />,
      title: "Advanced Bidet System",
      description: "Multi-pattern water spray with adjustable pressure, temperature control, and self-cleaning nozzles"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Touch-Free Operation",
      description: "Motion sensor technology for automatic lid opening, seat activation, and hands-free flushing"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="LUMA Smart Toilet | LED Ambient Bathroom Lighting"
        description="LUMA smart toilet with dynamic LED ambiance lighting, self-cleaning bidet nozzles and touch-free motion-sensor operation. CE certified, 5-year warranty."
        keywords="luxury smart toilet LED lighting, LUMA Series illuminated toilet, ambient LED bathroom fixtures, dynamic lighting smart toilet, touch-free bidet system, motion sensor luxury toilet, RGB bathroom lighting, smart ambiance control, intelligent bathroom technology, premium LED toilet, European smart sanitary systems, ambient lighting toilet, auto flush motion sensor, heated toilet seat control, bidet functionality, self-cleaning nozzle system"
        url="/products/luma"
        image="https://www.aureum7trading.com/images/products/luma-series-product.jpg"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "LUMA Series", url: "/products/luma" }
        ]}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "LUMA Series Smart Toilet",
          "description": "Luxury smart toilet with dynamic LED lighting, intelligent ambiance control, and advanced bidet technology for sophisticated bathroom experiences",
          "brand": {
            "@type": "Brand",
            "name": "Aureum VII Trading"
          },
          "image": [
            "https://www.aureum7trading.com/images/products/luma-series-product.jpg"
          ],
          "gtin13": "0795479810010",
          "manufacturer": {
            "@type": "Organization",
            "name": "Aureum VII Trading"
          },
          "sku": "LUMA-2024",
          "mpn": "LUMA",
          "gtin": "AVT-LUM-010",
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "priceCurrency": "EUR",
            "price": 600,
            "priceValidUntil": "2026-12-31",
            "itemCondition": "https://schema.org/NewCondition",
            "url": "https://www.aureum7trading.com/products/luma",
            "seller": {
              "@type": "Organization",
              "name": "Aureum VII Trading",
              "url": "https://www.aureum7trading.com"
            },
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": {
                "@type": "MonetaryAmount",
                "value": "0",
                "currency": "EUR"
              },
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 1,
                  "maxValue": 3,
                  "unitCode": "DAY"
                },
                "transitTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 10,
                  "maxValue": 40,
                  "unitCode": "DAY"
                }
              }
            },
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "@id": "https://www.aureum7trading.com/return-policy",
              "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
              "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
              "merchantReturnDays": 14,
              "returnMethod": "https://schema.org/ReturnByMail",
              "returnFees": "https://schema.org/FreeReturn",
              "itemCondition": "https://schema.org/NewCondition",
              "refundType": "https://schema.org/FullRefund"
            }
          },
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "LED Lighting",
              "value": "Dynamic RGB with 16+ Color Modes"
            },
            {
              "@type": "PropertyValue",
              "name": "Motion Sensors",
              "value": "Touch-Free Operation"
            },
            {
              "@type": "PropertyValue",
              "name": "Warranty",
              "value": "5 Years Comprehensive"
            }
          ]
        }}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-marble">
        <div className="container mx-auto max-w-7xl">
          <BreadcrumbNav items={[
            { label: "Home", href: "/" },
            { label: "Smart Toilets", href: "/smart-toilets" },
            { label: "LUMA Series" }
          ]} />
          
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mt-8">
            <div className="fade-in-observer">
              <div className="mb-6">
                <Badge className="bg-accent/10 text-accent-foreground border-accent/20 mb-4">
                  Tech-Forward Collection
                </Badge>
                <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 text-primary leading-tight">
                  LUMA
                  <span className="block text-2xl md:text-3xl text-muted-foreground font-medium mt-2">
                    Illuminated Innovation
                  </span>
                </h1>
                <p className="font-inter text-lg text-muted-foreground leading-relaxed mb-8">
                  The LUMA Series luxury <Link to="/smart-toilets" className="text-primary hover:underline font-semibold">smart toilet</Link> revolutionizes modern bathroom design with dynamic LED lighting technology and intelligent ambiance control. This premium European smart bathroom fixture features customizable RGB illumination with 16+ color modes, advanced bidet system with self-cleaning technology, touch-free motion sensor operation, and mood-responsive lighting patterns. Perfect for <Link to="/blog/luxury-smart-toilet-guide" className="text-primary hover:underline">luxury smart bathroom</Link> installations seeking sophisticated ambient lighting combined with high-tech sanitary solutions. Experience the perfect fusion of illumination artistry and intelligent hygiene automation. Compare with our flagship <Link to="/products/luxa" className="text-primary hover:underline">LUXA M80</Link> or explore our complete <Link to="/blog/smart-toilet-vs-bidet" className="text-primary hover:underline font-semibold">smart toilet vs bidet comparison guide</Link>.
                </p>
              </div>
              
              <PriceFrom price={600} />

              {/* Features List */}
              <div className="mb-8 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">16+ RGB color modes with brightness control</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Time-of-day adaptive lighting</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Motion-activated touch-free operation</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Advanced bidet with heated water</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <PDFDownloadButton productName="LUMA" />
                <Button variant="outline" asChild>
                  <Link to="/smart-toilets">← Back to Collections</Link>
                </Button>
              </div>
              
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
                <div className="text-center">
                  <div className="font-playfair text-2xl font-bold text-primary">16+</div>
                  <div className="font-inter text-sm text-muted-foreground">Color Modes</div>
                </div>
                <div className="text-center">
                  <div className="font-playfair text-2xl font-bold text-primary">5 Yrs</div>
                  <div className="font-inter text-sm text-muted-foreground">Warranty</div>
                </div>
                <div className="text-center">
                  <div className="font-playfair text-2xl font-bold text-primary">CE</div>
                  <div className="font-inter text-sm text-muted-foreground">Certified</div>
                </div>
              </div>
            </div>
            
            <div className="fade-in-observer">
              <ImageLightbox
                src={lumaSeriesProduct}
                alt="LUMA Series luxury smart toilet featuring dynamic LED lighting, intelligent ambiance control, and advanced bidet technology"
                className="w-full h-96 sm:h-[500px] lg:h-[600px] object-cover rounded-lg shadow-elegant cursor-pointer hover:shadow-gold transition-all duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 sm:mb-16 fade-in-observer">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Illuminated Innovation
            </h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              The LUMA series combines cutting-edge LED lighting technology with sophisticated smart toilet 
              features for an unparalleled bathroom experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <article key={index} className="fade-in-observer rounded-lg border bg-card shadow-elegant hover:shadow-gold transition-all duration-500 hover:-translate-y-2 p-8 text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-accent/10 rounded-full flex items-center justify-center text-accent">
                  {feature.icon}
                </div>
                <h3 className="font-playfair text-xl font-bold text-primary">{feature.title}</h3>
                <p className="font-inter text-muted-foreground leading-relaxed">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-marble">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 sm:mb-16 fade-in-observer">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Design Excellence
            </h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              Witness the perfect harmony of illumination and functionality in every detail.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="fade-in-observer">
              <ImageLightbox
                src={lumaSeriesProductSide}
                alt="LUMA smart toilet side profile showing sleek design with integrated LED lighting system"
                className="w-full h-80 object-cover rounded-lg shadow-elegant hover:shadow-gold transition-all duration-500 mb-4"
              />
              <h3 className="font-playfair text-xl font-bold text-primary mb-2">Side Profile</h3>
              <p className="font-inter text-muted-foreground">Sleek lines with integrated LED lighting</p>
            </div>

            <div className="fade-in-observer">
              <ImageLightbox
                src={lumaSeriesProductOpen}
                alt="LUMA smart toilet open seat view showcasing bidet nozzles and self-cleaning technology"
                className="w-full h-80 object-cover rounded-lg shadow-elegant hover:shadow-gold transition-all duration-500 mb-4"
              />
              <h3 className="font-playfair text-xl font-bold text-primary mb-2">Advanced Features</h3>
              <p className="font-inter text-muted-foreground">Premium bidet system with self-cleaning nozzles</p>
            </div>

            <div className="fade-in-observer">
              <ImageLightbox
                src={lumaSeriesLifestyle}
                alt="LUMA luxury smart toilet installed in modern bathroom with ambient LED lighting"
                className="w-full h-80 object-cover rounded-lg shadow-elegant hover:shadow-gold transition-all duration-500 mb-4"
              />
              <h3 className="font-playfair text-xl font-bold text-primary mb-2">Lifestyle Integration</h3>
              <p className="font-inter text-muted-foreground">Perfect for modern luxury living spaces</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16 fade-in-observer">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Technical Excellence
            </h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              Engineered with precision for optimal performance and luxury experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <div className="fade-in-observer rounded-lg border bg-card shadow-elegant p-8 space-y-4">
              <h3 className="font-playfair text-2xl font-bold mb-6 text-primary">LUMA Specifications</h3>
              {specifications.map((spec, index) => (
                <div key={index} className="flex justify-between items-center py-3 border-b border-border last:border-b-0">
                  <span className="font-inter font-medium text-muted-foreground">{spec.label}</span>
                  <span className="font-inter text-primary font-semibold text-right max-w-[60%]">{spec.value}</span>
                </div>
              ))}
            </div>

            <div className="space-y-6 fade-in-observer">
              <div className="rounded-lg border bg-card shadow-elegant p-6">
                <h4 className="font-playfair text-lg font-semibold text-primary mb-4">LED Lighting System</h4>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">16+ customizable RGB color modes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Moon className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">Adaptive brightness for day/night</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">Mood-responsive light patterns</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border bg-card shadow-elegant p-6">
                <h4 className="font-playfair text-lg font-semibold text-primary mb-4">Smart Features</h4>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">Motion sensor touch-free operation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Thermometer className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">Heated seat with temperature control</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">Self-cleaning nozzle technology</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border bg-card shadow-elegant p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-playfair text-lg font-semibold text-primary">Premium Quality</h4>
                  <p className="font-inter text-sm text-muted-foreground">CE Certified with 5-Year Warranty</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping & Delivery Information */}
      <section className="py-16 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <ShippingTimeInfo variant="detailed" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-elegant text-white">
        <div className="container mx-auto max-w-4xl text-center fade-in-observer">
          <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
            Illuminate Your Luxury Bathroom
          </h2>
          <p className="font-inter text-lg mb-8 max-w-2xl mx-auto opacity-90">
            Transform your bathroom into a sophisticated sanctuary with LUMA's dynamic LED lighting 
            technology and intelligent smart toilet features.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-primary hover:bg-white/90 font-inter font-semibold px-8 py-3 shadow-lg">
                Request Consultation
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/smart-toilets">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 font-inter font-semibold px-8 py-3">
                Explore All Collections
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <RelatedProducts currentProduct="LUMA" />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Luma;